import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/lib/queryClient";
import { adminSetSellerActive, adminUpdateSeller } from "@/lib/callables";
import type {
  Product,
  Seller,
  SellerBilling,
  SellerBillingStatus,
  SellerRating,
  Order,
} from "@/types";
import { fetchAggregate, fetchCollection, fetchDocument, orderBy, where } from "./use-firestore";
import { usePagedQuery } from "./use-paged-query";

export function useSellers() {
  return useQuery({
    queryKey: queryKeys.sellers(),
    // Sem `orderBy` na consulta: o campo `name` pode faltar em documento
    // antigo, e o Firestore OMITE o documento cujo campo de ordenação não
    // existe. Numa tela de gestão, sumir com um vendedor é pior que carregar
    // tudo e ordenar aqui.
    queryFn: () => fetchCollection<Seller>("sellers"),
    select: (rows) =>
      [...rows].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "", "pt-BR")),
  });
}

export function useSeller(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.seller(id ?? ""),
    queryFn: () => fetchDocument<Seller>("sellers", id as string),
    enabled: Boolean(id),
  });
}

export function useSellerProducts(sellerId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.sellerProducts(sellerId ?? ""),
    queryFn: () =>
      fetchCollection<Product>("products", where("sellerId", "==", sellerId as string)),
    enabled: Boolean(sellerId),
    select: (rows) =>
      [...rows].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "", "pt-BR")),
  });
}

export function useSellerRatings(sellerId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.sellerRatings(sellerId ?? ""),
    queryFn: () =>
      fetchCollection<SellerRating>("sellerRatings", where("sellerId", "==", sellerId as string)),
    enabled: Boolean(sellerId),
  });
}

export function useSellerBilling(sellerId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.sellerBilling(sellerId ?? ""),
    queryFn: () =>
      fetchCollection<SellerBilling>(
        "sellersBilling",
        where("sellerId", "==", sellerId as string),
        orderBy("year", "desc"),
        orderBy("month", "desc"),
      ),
    enabled: Boolean(sellerId),
  });
}

export function useSellerBillingStatus(sellerId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.sellerBillingStatus(sellerId ?? ""),
    queryFn: () =>
      fetchDocument<SellerBillingStatus>("sellersBillingStatus", sellerId as string),
    enabled: Boolean(sellerId),
  });
}

/**
 * O histórico de vendas do estabelecimento, uma página por vez, do mais recente
 * para o mais antigo. Antes eram os cem mais recentes, e os números do topo da
 * aba (entregues, receita) eram calculados só sobre eles.
 */
export function useSellerOrders(sellerId: string | undefined, total?: number) {
  return usePagedQuery<Order>({
    key: queryKeys.sellerOrders(sellerId ?? ""),
    path: "orders",
    constraints: [where("sellerId", "==", sellerId ?? ""), orderBy("createdAt", "desc")],
    enabled: Boolean(sellerId),
    total,
  });
}

/**
 * Os números de toda a vida do estabelecimento, por agregação no servidor:
 * total de pedidos, e quantos foram entregues e quanto renderam. Usam os
 * índices `sellerId` mais `createdAt` e `sellerId`, `status` e `createdAt`.
 */
export function useSellerSalesSummary(sellerId: string | undefined) {
  return useQuery({
    queryKey: [...queryKeys.sellerOrders(sellerId ?? ""), "summary"],
    queryFn: async () => {
      const id = sellerId as string;
      const [all, delivered] = await Promise.all([
        fetchAggregate("orders", [where("sellerId", "==", id), orderBy("createdAt", "desc")]),
        fetchAggregate(
          "orders",
          [where("sellerId", "==", id), where("status", "==", "delivered"), orderBy("createdAt", "desc")],
          "total",
        ),
      ]);
      return {
        totalOrders: all.count,
        deliveredOrders: delivered.count,
        deliveredRevenue: delivered.sum,
      };
    },
    enabled: Boolean(sellerId),
  });
}

export function useUpdateSeller() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { sellerId: string; data: Record<string, unknown> }) =>
      adminUpdateSeller(input),
    onSuccess: (_result, input) => {
      qc.invalidateQueries({ queryKey: queryKeys.seller(input.sellerId) });
      qc.invalidateQueries({ queryKey: queryKeys.sellers() });
    },
  });
}

export function useSetSellerActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { sellerId: string; isActive: boolean; reason?: string }) =>
      adminSetSellerActive(input),
    onSuccess: (_result, input) => {
      qc.invalidateQueries({ queryKey: queryKeys.seller(input.sellerId) });
      qc.invalidateQueries({ queryKey: queryKeys.sellers() });
    },
  });
}
